import { Divider, styled } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { InlineEditText } from '../inputs/InlineEditText'
import { useCallback, useRef } from 'react'
import { PlaceholderText } from '../commons'
import { intl } from '../../../../../i18n'
import { ExpandableText } from '../ExpandableText'
import { useSaveIndicator } from '../save-indicator/SaveIndicatorProvider'

export const PageRoot = styled('div')({
  backgroundColor: colorPalette.monotone[0],
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 24px',
  height: '100%',
  gap: '8px',
  position: 'relative',
})

const PageHeaderArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
})

const PageTitleArea = styled('div')({
  display: 'flex',
  height: '26px',
  alignItems: 'center',
  borderRadius: '4px',
})
const PageTitle = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: '18px',
  fontWeight: 600,
  lineHeight: 1,
})

const PageHeaderDivider = styled(Divider)({
  borderColor: colorPalette.monotone[2],
})

const LeadSentenceArea = styled('div')({
  height: '50px',
  borderRadius: '4px',
})
const LeadSentence = styled('div')({
  color: colorPalette.monotone[9],
  fontSize: '14px',
  lineHeight: 1.5,
  overflowWrap: 'break-word',
  height: '100%',
  overflowY: 'scroll',
})

type PageHeaderProps = {
  pageTitle: string
  leadSentence: string | undefined
  onChangePageTitle: (v: string) => void
  onChangeLeadSentence: (v: string) => void
  onBlurPageTitle: (v: string) => Promise<boolean>
  onBlurLeadSentence: (v: string) => Promise<boolean>
  editable: boolean
}
export const PageHeader = ({
  pageTitle,
  leadSentence,
  onChangePageTitle: _onChangePageTitle,
  onChangeLeadSentence: _onChangeLeadSentence,
  onBlurPageTitle: _onBlurPageTitle,
  onBlurLeadSentence: _onBlurLeadSentence,
  editable,
}: PageHeaderProps) => {
  const { showSavePill } = useSaveIndicator()
  const titleRef = useRef<HTMLDivElement>(null)
  const leadRef = useRef<HTMLDivElement>(null)
  const onChangePageTitle = useCallback(
    (v: string | undefined) => {
      _onChangePageTitle(v || '')
    },
    [_onChangePageTitle]
  )
  const onChangeLeadSentence = useCallback(
    (v: string | undefined) => {
      _onChangeLeadSentence(v || '')
    },
    [_onChangeLeadSentence]
  )
  const onBlurPageTitle = useCallback(
    async (v: string | undefined, changed: boolean) => {
      const saved = await _onBlurPageTitle(v || '')
      if (saved && changed) {
        showSavePill({ key: 'pageTitle', anchorEl: titleRef.current })
      }
    },
    [_onBlurPageTitle, showSavePill]
  )
  const onBlurLeadSentence = useCallback(
    async (v: string | undefined, changed: boolean) => {
      const saved = await _onBlurLeadSentence(v || '')
      if (saved && changed) {
        showSavePill({ key: 'leadSentence', anchorEl: leadRef.current })
      }
    },
    [_onBlurLeadSentence, showSavePill]
  )
  return (
    <PageHeaderArea>
      <PageTitleArea ref={titleRef}>
        <InlineEditText
          value={pageTitle}
          display={<PageTitle>{pageTitle}</PageTitle>}
          onChange={onChangePageTitle}
          onBlur={onBlurPageTitle}
          editable={editable}
          variant="header"
          validate={v => !!v?.trim()}
        />
      </PageTitleArea>
      <PageHeaderDivider />
      <LeadSentenceArea ref={leadRef}>
        <InlineEditText
          value={leadSentence}
          display={
            leadSentence ? (
              <ExpandableText value={leadSentence} />
            ) : (
              <LeadSentence>
                <PlaceholderText>
                  {intl.formatMessage({
                    id: 'versionReport.leadSentence.placeholder',
                  })}
                </PlaceholderText>
              </LeadSentence>
            )
          }
          onChange={onChangeLeadSentence}
          onBlur={onBlurLeadSentence}
          editable={editable}
          variant="leadSentence"
          multiline={true}
          placeholder={intl.formatMessage({
            id: 'versionReport.leadSentence.placeholder',
          })}
        />
      </LeadSentenceArea>
    </PageHeaderArea>
  )
}
